import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Brain, X, Send, Loader2, MessageSquare, Pencil } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const API = process.env.REACT_APP_BACKEND_URL;

const CONSULT_SUGGESTIONS = ['Explica mis numeros', 'Analiza riesgos', 'Puntos de mejora'];
const MODIFY_SUGGESTIONS = ['Aumenta ventas 10%', 'Agrega un producto', 'Reduce gastos fijos 15%'];

const ChatAssistant = () => {
  const { user, hasActivePlan } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('consult'); // 'consult' | 'modify'
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `unified-${Date.now()}`);
  const scrollRef = useRef(null);

  const isSubscribed = hasActivePlan ? hasActivePlan() : false;
  const projectId = location.pathname.match(/project\/([^/]+)/)?.[1] || null;

  // Reset messages when panel opens or project changes
  useEffect(() => {
    if (open) {
      const welcomeMsg = projectId
        ? `Estoy listo para **${mode === 'modify' ? 'modificar' : 'analizar'}** tu proyecto. ${mode === 'modify' ? 'Dime que cambios quieres aplicar.' : 'Preguntame lo que necesites.'}`
        : 'Hola, soy tu **Analista IA**. Para consultar o modificar un proyecto, primero abre uno desde el Inicio. Para **crear un proyecto nuevo**, ve a **Analista IA** en el menu.';
      setMessages([{ role: 'assistant', content: welcomeMsg }]);
    }
  }, [open, projectId, mode]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    if (!isSubscribed) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Esta funcion requiere un plan activo. Consulta los planes en la seccion de **Precios**.' }]);
      setLoading(false);
      return;
    }

    // Detect if user wants to create a new project
    const lower = text.toLowerCase();
    const wantsGenerate = lower.includes('genera') || lower.includes('crear proyecto') || lower.includes('quiero poner') || lower.includes('nuevo negocio') || lower.includes('nuevo proyecto') || lower.includes('crear un proyecto') || lower.includes('proyecto nuevo') || lower.includes('desde cero');

    if (wantsGenerate) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Para **crear un proyecto desde cero**, usa el **Analista IA** desde el menu principal. Yo solo puedo **consultar** o **hacer cambios** sobre proyectos que ya existen.'
      }]);
      setLoading(false);
      return;
    }

    if (!projectId) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Necesitas tener un proyecto abierto para que pueda ayudarte. Abre un proyecto desde el **Inicio** o crea uno nuevo en **Analista IA**.'
      }]);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      if (mode === 'modify') {
        await axios.post(`${API}/api/ai-analyst/modify`, {
          project_id: projectId,
          instruction: text,
          session_id: sessionId
        }, { headers, timeout: 120000 });
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Listo, aplique los cambios. La pagina se actualizara para que veas el resultado.`
        }]);
        setTimeout(() => { window.location.reload(); }, 2500);
      } else {
        const res = await axios.post(`${API}/api/ai-analyst/chat`, {
          project_id: projectId,
          instruction: text,
          session_id: sessionId
        }, { headers, timeout: 60000 });
        setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al procesar tu mensaje';
      setMessages(prev => [...prev, { role: 'assistant', content: `Lo siento: ${detail}` }]);
    } finally {
      setLoading(false);
    }
  };

  const isModify = mode === 'modify';

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-fab"
          className="fixed bottom-36 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-lg shadow-purple-600/30 flex items-center justify-center transition-all hover:scale-110"
        >
          <Brain className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-36 right-6 z-50 w-[400px] h-[520px] bg-[#0b0f1a] border border-border/50 rounded-2xl shadow-2xl shadow-purple-900/20 flex flex-col overflow-hidden" data-testid="chat-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/30 bg-gradient-to-r from-[#0d1225] to-[#13152a]">
            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isModify ? 'bg-gradient-to-br from-violet-500 to-fuchsia-600' : 'bg-gradient-to-br from-blue-500 to-purple-600'}`}>
                <Brain className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Analista IA</p>
                <p className="text-[10px] text-gray-400">
                  {!projectId ? 'Abre un proyecto primero' : isModify ? 'Modo: Aplicando cambios' : 'Modo: Consultando'}
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white transition-colors" data-testid="chat-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mode Toggle */}
          {projectId && (
            <div className="px-3 py-2 border-b border-border/20 flex gap-1.5" data-testid="chat-mode-toggle">
              <button
                onClick={() => setMode('consult')}
                data-testid="chat-mode-consult"
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  !isModify
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                    : 'text-gray-500 hover:text-gray-300 border border-transparent'
                }`}
              >
                <MessageSquare className="w-3 h-3" />
                Consultar
              </button>
              <button
                onClick={() => setMode('modify')}
                data-testid="chat-mode-modify"
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  isModify
                    ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                    : 'text-gray-500 hover:text-gray-300 border border-transparent'
                }`}
              >
                <Pencil className="w-3 h-3" />
                Modificar
              </button>
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[88%] px-3.5 py-2.5 rounded-xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? `${isModify ? 'bg-violet-600' : 'bg-blue-600'} text-white rounded-br-sm`
                    : 'bg-[#161b2e] text-gray-200 rounded-bl-sm border border-border/20'
                }`}>
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-sm prose-invert max-w-none [&>p]:mb-1.5 [&>ul]:mb-1.5 [&>ul]:ml-4 [&>p:last-child]:mb-0">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#161b2e] border border-border/20 px-4 py-3 rounded-xl rounded-bl-sm flex items-center gap-2">
                  <Loader2 className={`w-4 h-4 animate-spin ${isModify ? 'text-violet-400' : 'text-purple-400'}`} />
                  <span className="text-xs text-gray-400">{isModify ? 'Investigando y aplicando cambios...' : 'Analizando...'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Quick actions */}
          {projectId && messages.length <= 2 && (
            <div className="px-3 py-2 border-t border-border/20 flex gap-1.5 flex-wrap">
              {(isModify ? MODIFY_SUGGESTIONS : CONSULT_SUGGESTIONS).map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(q); }}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    isModify
                      ? 'bg-violet-500/10 border-violet-500/20 text-violet-300 hover:bg-violet-500/20'
                      : 'bg-blue-500/10 border-blue-500/20 text-blue-300 hover:bg-blue-500/20'
                  }`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-3 py-2.5 border-t border-border/30 bg-[#0d1225]">
            <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={isModify ? 'Ej: Aumenta el precio del cafe a $80...' : 'Ej: Explica mi TIR y VAN...'}
                className={`flex-1 bg-[#161b2e] border rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 ${
                  isModify
                    ? 'border-violet-500/30 focus:ring-violet-500/50'
                    : 'border-border/30 focus:ring-blue-500/50'
                }`}
                disabled={loading}
                data-testid="chat-input"
              />
              <Button
                type="submit"
                size="sm"
                disabled={loading || !input.trim()}
                className={`px-3 ${isModify ? 'bg-violet-600 hover:bg-violet-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                data-testid="chat-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatAssistant;
